import { Directive, ElementRef, Input, NgModule, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { execWhenConnected, isBrowser, isNotEmptyOrHiddenElement, NodeObserver, observeNode, WRxBase } from '@wipo/w-angular/shared';
import { merge } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';


@Directive({
  selector: '[hideEmpty],[hide-empty],[hide-empty-if]',
  standalone: false
})
export class HideEmptyDirective extends WRxBase implements OnInit {
  // tslint:disable-next-line: variable-name
  _depth = 2;
  hidden = false;

  get depth() {
    return this._depth;
  }
  @Input('hide-empty') set depthAny(val: any) {
    // receive either empty string or numeric value
    this._depth = (val === '') ? 1 : +val;
  }
  @Input() set hideEmpty(value: number) { this._depth = value; }

  private showIf = true;   // true with condition that either used fixed facets or self or children should be available
  private noWatch = false; // watching by default, disable if parent provides fixed facets

  @Input() facet?: string; // context (can add tracing)
  /* disable mutation watching, set start depth and add show condition (on top of has-content) */
  @Input('hide-empty-if') set hideEmptyIf(value: (string | [boolean, number?, boolean?])) {
    setTimeout(() => {
    // if no parameter (empty string if we do not assign value), use default array
    const params = typeof value === 'string' ? [false] as any[] : value;
    this.noWatch = params[0];
    this._depth = params[1] ?? 2; // default depth
    this.showIf = params[2] ?? true; // condition to show if watch->NOT EMPTY
    // see if need start observing and check the visiblity
    this.initObserver(); // NOOP if noWatch
    if (!this.noWatch && this.observer) {
      this.showHide(this.observer, true); // NOOP if always hide if NOT showIf
    }}, 10);
  }

  me: HTMLElement;
  private observer!: NodeObserver;
  constructor(protected elRef: ElementRef) {
    super();
    this.me = this.elRef.nativeElement;
  }

  ngOnInit(): void {
    this.initObserver();
  }
  private initObserver(): void {
    if (isBrowser && !this.noWatch && !this.observer) { this.observeRoot(); }
  }
  showHide(nodeObserver: NodeObserver, hideFromDescendant?: boolean) {
    /** check on next cycle */
    const checkVisibility = () => {
      const hidden = this.hidden;
      if (hideFromDescendant === false && this.hidden) {
        // if child visible, this also is visible
        this.hidden = false;
      } else {
        // state might have changed, verify!
        const showCondition = this.showIf;
        const hideContent = this.hide(this.me, this.depth);
        this.hidden = !showCondition || hideContent;
      }

      // state changed, set style and notify parent
      if (nodeObserver && hidden !== this.hidden) {
        this.me.style.display = this.hidden ? 'none' : '';
        // lets CSS distinguish hidden-as-empty from :empty (element children are never :empty)
        this.me.classList.toggle('b-hidden-empty', this.hidden);
        nodeObserver.hideStateChange.next(this.hidden);
      }
    };
    checkVisibility();
  }
  observeRoot() {
    const init = () => {
      const nodeObserver = observeNode(this.me);
      /** wait while component is built but not attached to the DOM */
      this.observer = nodeObserver;
      const handler = this.showHide.bind(this, nodeObserver);
      // on mutation (lowest in hierachy)
      // any child has changed the visible state
      // on self hide state change (from parent)
      merge(nodeObserver.mutation, nodeObserver.childStateChange).pipe(
        filter(() => !this.noWatch),
        debounceTime(50) // 
      ).subscribe(handler);
      this.showHide(nodeObserver);
    };
    /** we wait until in DOM to start observing state */
    execWhenConnected(this.me, init);
  }

  /* hide if
    - depth -levels deep there are no children and 
    - the text is empty or a single space
  */
  hide(element: Element, depth: number): boolean {
    // current element can be hidden, we will check the children
    if (depth > 1) {
      // drill down
      return Array.from(element.children).every((e: Element) => this.hide(e, depth - 1));
    } else {
      // start from depth === 1
      const emptyOrHidden = !isNotEmptyOrHiddenElement(element, element === this.me);
      return emptyOrHidden;
    }
  }
}
@NgModule({
  exports: [HideEmptyDirective],
  declarations: [HideEmptyDirective]
})
export class WHideEmptyModule { }
